/**
 * Turns arbitrary QR content into a safe, readable filename stem.
 * Falls back to a constant so an all-punctuation payload cannot produce a
 * hidden or empty filename.
 */
export function toFilenameStem(content: string, maxLength = 32): string {
  const slug = content
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, maxLength)
    .replace(/-+$/, '');
  return slug.length > 0 ? slug : 'qr-code';
}

/** Triggers a browser download for `blob`, always revoking the object URL. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Deferred so the navigation has certainly started before the URL dies.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }
}

export function downloadSvg(svg: string, filename: string): void {
  downloadBlob(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }), filename);
}

/** Exports the canvas as a PNG. Resolves once the download has been triggered. */
export async function downloadCanvasAsPng(
  canvas: HTMLCanvasElement,
  filename: string,
): Promise<void> {
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });
  if (blob === null) return;
  downloadBlob(blob, filename);
}
